import React from 'react';

import { CircularProgress } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import AppSelectBox from 'components/FormElements/AppSelectBox';
import TextField from 'components/FormElements/AppTextInput';
import { MuiDateRangePicker } from 'components/FormElements/DateRangePicker';
import GridContainer from 'components/GridContainer';
import { Grid } from '@mui/material';
import { map, sortBy } from 'lodash';
import { getFeeAccountingOptions } from 'utils/getFeeAccountingOptions';
import TranchesMultiSelect from 'components/TrancheMultiSelect';
import { type as feeConst } from 'adminConstants/typeConstant';
import { useBackgroundExports } from 'hooks/data/backgroundExports/useBackgroundExports';
import { BackgroundExportTypes } from 'further-types/background-exports';
import { FeeAccounting } from 'further-types/investment';
import { FeeExistenceFilterType } from 'further-types/fees';
import Button from 'components/Button';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

export const optionValueAll = 'ALL';

const useStyles = makeStyles()(() => ({
  label: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  buttonContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    height: '100%',
    paddingBottom: 2,
  },
  actionButton: {
    marginRight: 8,
  },
  filterLoading: {
    paddingLeft: 6,
    paddingTop: 10,
  },
  exportSpinner: {
    color: 'rgba(0, 0, 0, 0.26)',
    marginRight: 10,
  },
}));

type Filters = {
  startDate?: Date;
  endDate?: Date;
  feeAccounting: typeof optionValueAll | FeeAccounting;
  feeType: FeeExistenceFilterType;
  funds: Array<string>;
  fullName: string;
  exportToCustodian: typeof optionValueAll | boolean;
};

type Props = {
  filters: Filters;
  onFiltersChange: (filters: Partial<Filters>) => void;
  trancheListing: Array<{
    _id: string;
    firmId: {
      _id: string;
      firmName: string;
    };
    fundName: string;
  }>;
  setIsOpen: (isOpen: boolean) => void;
  isExportLoading: boolean;
  order: 'asc' | 'desc';
  orderBy: string;
};

const FeeFilter: React.FC<Props> = ({
  filters,
  onFiltersChange,
  trancheListing,
  setIsOpen,
  isExportLoading,
  order,
  orderBy,
}) => {
  const { classes } = useStyles();
  const [canExportCustodianFees] = useHasPermissions([
    RolePermission.ExportCustodianFees,
  ]);
  const { createBackgroundExport, isExporting } = useBackgroundExports();

  const handleGeneralExport = () => {
    createBackgroundExport.mutate({
      exportType: BackgroundExportTypes.FeesExport,
      params: {
        exportToCustodian:
          filters.exportToCustodian === optionValueAll
            ? undefined
            : filters.exportToCustodian,
        feeAccounting:
          filters.feeAccounting === optionValueAll
            ? undefined
            : filters.feeAccounting,
        feeType: filters.feeType,
        funds: filters.funds,
        fullName: filters.fullName,
        startDate: filters.startDate
          ? filters.startDate.toISOString()
          : undefined,
        endDate: filters.endDate ? filters.endDate.toISOString() : undefined,
        order,
        orderBy,
      },
    });
  };

  const mappedTranches = map(
    sortBy(trancheListing ?? [], (x) => x.firmId.firmName.toLowerCase),
    (tranche) => ({
      ...tranche,
      label: `${tranche.firmId.firmName} ${tranche.fundName}`,
    }),
  );
  const feeAccounting = getFeeAccountingOptions();
  return (
    <GridContainer>
      <Grid size={{ xs: 12 }}>
        <GridContainer spacing={3}>
          <Grid size={{ xs: 3, xl: 2 }}>
            <label className={classes.label} htmlFor="dateRange">
              Date range
            </label>
            <MuiDateRangePicker
              dateRange={{
                startDate: filters.startDate,
                endDate: filters.endDate,
              }}
              onDateRangeChange={(dateRange) => {
                onFiltersChange(dateRange);
              }}
              placeholder="Select date"
            />
          </Grid>
          <Grid size={{ xs: 3, xl: 2 }}>
            <label className={classes.label} htmlFor="feeAccounting">
              Fee accounting
            </label>
            <AppSelectBox
              required
              data={[{ label: 'All', value: optionValueAll }, ...feeAccounting]}
              name="feeAccounting"
              valueKey="value"
              labelKey="label"
              variant="outlined"
              value={filters?.feeAccounting}
              fullWidth
              onChange={(event) => {
                onFiltersChange({
                  feeAccounting: event.target.value as
                    | FeeAccounting
                    | typeof optionValueAll,
                });
              }}
            />
          </Grid>
          <Grid size={{ xs: 3, xl: 2 }}>
            <label className={classes.label} htmlFor="feeType">
              Fee display
            </label>
            <AppSelectBox
              data={[
                {
                  label: 'Fees to date',
                  value: FeeExistenceFilterType.Past,
                },
                { label: 'Future fees', value: FeeExistenceFilterType.Future },
                { label: 'All', value: FeeExistenceFilterType.All },
              ]}
              name="feeType"
              valueKey="value"
              labelKey="label"
              variant="outlined"
              value={filters?.feeType}
              onChange={(event) => {
                onFiltersChange({
                  feeType: event.target.value as FeeExistenceFilterType,
                });
              }}
            />
          </Grid>
          <Grid size={{ xs: 3, xl: 2 }}>
            <label className={classes.label} htmlFor="funds">
              Tranche
            </label>
            {mappedTranches?.length ? (
              <TranchesMultiSelect
                tranches={mappedTranches}
                value={filters.funds}
                handleChangeSelectedTranches={(trancheIds) => {
                  onFiltersChange({ funds: trancheIds });
                }}
                selectAllLabel="Select all tranches (excludes investor balance fees)"
              />
            ) : (
              <div className={classes.filterLoading}>
                <CircularProgress size={18} />
              </div>
            )}
          </Grid>
          <Grid size={{ xs: 3, xl: 2 }}>
            <label className={classes.label} htmlFor="fullName">
              Fee name search
            </label>
            <TextField
              required
              name="fullName"
              placeholder="Enter name"
              value={filters?.fullName}
              fullWidth
              onChange={(event) => {
                onFiltersChange({
                  fullName: event.target.value,
                });
              }}
            />
          </Grid>
          <Grid size={{ xs: 3, xl: 2 }}>
            <label className={classes.label} htmlFor="exportToCustodian">
              Exported to custodian?
            </label>
            <AppSelectBox
              data={[
                {
                  label: 'Yes',
                  value: true,
                },
                { label: 'No', value: false },
                { label: 'All', value: optionValueAll },
              ]}
              name="exportToCustodian"
              valueKey="value"
              labelKey="label"
              variant="outlined"
              value={filters?.exportToCustodian}
              onChange={(event) => {
                onFiltersChange({
                  exportToCustodian: event.target.value as
                    | typeof optionValueAll
                    | boolean,
                });
              }}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <div className={classes.buttonContainer}>
              {canExportCustodianFees && (
                <Button
                  onClick={() => setIsOpen(true)}
                  color="primary"
                  className={classes.actionButton}
                  variant="contained"
                  disabled={
                    filters.exportToCustodian === true ||
                    filters?.feeAccounting !==
                      feeConst.feeAccounting.chargedToInvestor
                  }
                  loading={isExporting || isExportLoading}
                >
                  Custodian export
                </Button>
              )}
              <Button
                color="primary"
                variant="contained"
                className={classes.actionButton}
                onClick={handleGeneralExport}
                loading={isExportLoading || isExporting}
              >
                General export
              </Button>
            </div>
          </Grid>
        </GridContainer>
      </Grid>
    </GridContainer>
  );
};

export default FeeFilter;
